import type { EligiblePackageNotificationDTO } from "../../domain/contracts/triggers";
import { buildNotificationMessage } from "../../domain/services/build-notification-message";
import { WhatsAppLinkButton } from "@/shared/ui/WhatsAppLinkButton";
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import { OffsetPaginationBar } from "@/shared/ui/OffsetPaginationBar";
import { maskPhoneNumber, TRIGGER_LABELS } from "./WorkerStatusBanner";

type RowKey = string;

function rowKey(item: EligiblePackageNotificationDTO): RowKey {
  return `${item.studentId}:${item.packageType}:${item.packageId}`;
}

function PackageTypeBadge({
  packageType,
}: {
  packageType: EligiblePackageNotificationDTO["packageType"];
}) {
  if (packageType === "package") {
    return (
      <Badge
        variant="outline"
        className="border-blue-500/40 bg-blue-500/10 text-blue-700"
      >
        ຄລາສ
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className="border-violet-500/40 bg-violet-500/10 text-violet-700"
    >
      ສ່ວນຕົວ
    </Badge>
  );
}

function TriggerBadge({
  triggerType,
}: {
  triggerType: EligiblePackageNotificationDTO["triggerType"];
}) {
  const label = TRIGGER_LABELS[triggerType] ?? triggerType;

  const className =
    triggerType === "expiring_soon"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-800"
      : triggerType === "combined"
        ? "border-purple-500/40 bg-purple-500/10 text-purple-700"
        : "border-slate-500/30 bg-muted/60 text-foreground";

  return (
    <Badge variant="outline" className={className}>
      {label}
    </Badge>
  );
}

function ExpiryCell({
  expireDate,
  daysUntilExpiry,
}: {
  expireDate: string;
  daysUntilExpiry: number;
}) {
  const urgent = daysUntilExpiry <= 3;

  return (
    <div className="space-y-1">
      <div className="tabular-nums">{expireDate.slice(0, 10)}</div>
      <Badge
        variant="outline"
        className={cn(
          "text-[10px] tabular-nums",
          urgent
            ? "border-destructive/40 bg-destructive/10 text-destructive"
            : "text-muted-foreground",
        )}
      >
        {daysUntilExpiry <= 0
          ? "ໝົດອາຍຸແລ້ວ"
          : `ອີກ ${daysUntilExpiry} ວັນ`}
      </Badge>
    </div>
  );
}

export function EligibleNotificationsTable({
  items,
  selected,
  onSelectedChange,
  onPreview,
  canSend,
  totalCount,
  offset,
  limit,
  filteredTotal,
  onPaginationChange,
}: {
  items: EligiblePackageNotificationDTO[];
  selected: Set<RowKey>;
  onSelectedChange: (next: Set<RowKey>) => void;
  onPreview: (items: EligiblePackageNotificationDTO[]) => void;
  canSend: boolean;
  totalCount?: number;
  offset?: number;
  limit?: number;
  filteredTotal?: number;
  onPaginationChange?: (offset: number) => void;
}) {
  const allSelected = items.length > 0 && selected.size === items.length;

  const toggleAll = () => {
    if (allSelected) {
      onSelectedChange(new Set());
      return;
    }
    onSelectedChange(new Set(items.map(rowKey)));
  };

  const toggleOne = (key: RowKey) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectedChange(next);
  };

  const selectedItems = items.filter((item) => selected.has(rowKey(item)));
  const colSpan = canSend ? 9 : 8;

  return (
    <div className="space-y-3">
      {canSend ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-sm">
            ເລືອກ {selectedItems.length.toLocaleString("lo-LA")} ລາຍການ
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selectedItems.length === 0}
            onClick={() => onPreview(selectedItems.slice(0, 3))}
          >
            <EyeIcon className="mr-1 size-4" />
            Preview ({selectedItems.length || 0})
          </Button>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              {canSend ? (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </TableHead>
              ) : null}
              <TableHead>ນັກຮຽນ</TableHead>
              <TableHead>ແພັກ</TableHead>
              <TableHead>ປະເພດ</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>ເຫຼືອ</TableHead>
              <TableHead>ໝົດອາຍຸ</TableHead>
              <TableHead>ເບີ</TableHead>
              <TableHead className="text-right">ການກະທຳ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="py-12 text-center"
                >
                  <p className="font-medium text-muted-foreground">
                    ບໍ່ມີລາຍການທີ່ເຂົ້າເງື່ອນໄຂ
                  </p>
                  {totalCount != null && totalCount > 0 ? (
                    <p className="mt-1 text-muted-foreground text-sm">
                      ລອງປັບກັ່ນຕົວ ຫຼື ລ້າງການຄົ້ນຫາ
                    </p>
                  ) : null}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const key = rowKey(item);
                const message = buildNotificationMessage({
                  studentName: item.studentName,
                  packageName: item.packageName,
                  packageType: item.packageType,
                  triggerType: item.triggerType,
                  remainingSessions: item.remainingSessions,
                  remainingHours: item.remainingHours,
                  expireDate: item.expireDate,
                  daysUntilExpiry: item.daysUntilExpiry,
                });
                return (
                  <TableRow key={key} className="hover:bg-muted/20">
                    {canSend ? (
                      <TableCell>
                        <Checkbox
                          checked={selected.has(key)}
                          onCheckedChange={() => toggleOne(key)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <div className="font-medium">{item.studentName}</div>
                      <div className="text-muted-foreground text-xs tabular-nums">
                        {item.memberId}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="truncate font-medium">{item.packageName}</div>
                    </TableCell>
                    <TableCell>
                      <PackageTypeBadge packageType={item.packageType} />
                    </TableCell>
                    <TableCell>
                      <TriggerBadge triggerType={item.triggerType} />
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {item.packageType === "package" ? (
                        <span className="font-medium">
                          {item.remainingSessions ?? 0}
                        </span>
                      ) : (
                        <span className="font-medium">
                          {item.remainingHours ?? 0}
                        </span>
                      )}
                      <span className="ml-1 text-muted-foreground text-xs">
                        {item.packageType === "package" ? "ຄັ້ງ" : "ຊມ."}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ExpiryCell
                        expireDate={item.expireDate}
                        daysUntilExpiry={item.daysUntilExpiry}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {maskPhoneNumber(item.phoneNumber)}
                    </TableCell>
                    <TableCell className="text-right">
                      <WhatsAppLinkButton
                        phone={item.phoneNumber}
                        message={message}
                        label="wa.me"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {offset != null && limit != null && filteredTotal != null && onPaginationChange ? (
        <OffsetPaginationBar
          offset={offset}
          limit={limit}
          total={filteredTotal}
          onChange={onPaginationChange}
        />
      ) : null}
    </div>
  );
}

export { rowKey as eligibleNotificationRowKey };
